// external libs
import { ofType } from 'redux-observable'
import { from, of } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import moment from 'moment'
import Immutable from 'seamless-immutable'
import GuardXLogger from 'guard-x-logger'

import SuriApi from '../../Services/SuriApi'
import { config } from '../../Config/ConfigLoader'
import logDataSerializer from '../Serializers/LogDataSerializer'
// redux
import ProductActions, { ProductTypes } from '../Redux/ProductRedux'
import OfferActions, { OfferTypes } from '../Redux/OfferRedux'
import GeneralActions, { ERROR_TYPES } from '../Redux/GeneralRedux'
import InputHelpsActions from '../Redux/InputHelpsRedux'
import NavigationActions from '../Redux/NavigationRedux'
import ValidationActions from '../Redux/ValidationRedux'

import { getStepUrl } from '../ContainerFunctions'

const CalculatorEpics = [
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CALCULATOR_SPECIFICATION_REQUEST),
      switchMap((action) =>
        from(SuriApi.calculatorSpecification(action.calculatorName)).pipe(
          switchMap((response) => of(ProductActions.calculatorSpecificationRequestSuccess(response.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CALCULATOR_CONTENT_REQUEST),
      switchMap((action) =>
        from(
          action.isBackOffice
            ? SuriApi.calculatorContentBackOffice(action.calculatorName)
            : SuriApi.calculatorContent(action.calculatorName),
        ).pipe(
          switchMap((response) => {
            let data = {}
            if (action.isBackOffice) {
              data = new Immutable(response.displayNames.data.data)
                .merge(response.displayNamesBackOffice.data.data, { deep: true })
                .asMutable()
            } else {
              data = response.data.data
            }
            const result = [ProductActions.calculatorContentRequestSuccess(data)]
            if (data && data.inputHelps) {
              result.push(InputHelpsActions.calculatorInputHelpsMerge(data.inputHelps))
            }
            return from(result)
          }),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('quotes'),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CONTRACT_DRAFT_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.contactDraftContent(action.calculatorName)).pipe(
          switchMap((response) => of(ProductActions.contractDraftContentRequestSuccess(response.data.data))),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('quotes'),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CALCULATOR_RESOURCES_REQUEST),
      switchMap((action) =>
        from(SuriApi.calculatorResources(action.calculatorName)).pipe(
          switchMap((response) => {
            const data = response.data.data
            delete data.coverages
            return of(ProductActions.resourcesRequestSuccess(data))
          }),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('quotes'),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CALCULATOR_COVERAGES_REQUEST),
      switchMap((action) =>
        from(SuriApi.coveragesNames(action.calculatorName)).pipe(
          switchMap((response) =>
            of(
              ProductActions.resourcesRequestSuccess({
                'coverages-names': response.data.data,
              }),
            ),
          ),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('quotes'),
            ])
          }),
        ),
      ),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(OfferTypes.CONTRACT_DRAFT_REQUEST),
      switchMap((action) => {
        const properties = state$.value.userData.getIn(['properties'], {})
        return from(
          SuriApi.calculatorContractDraft(action.calculatorName, {
            ...action.productDescriptorValues,
            // filter for properties
            properties: Object.keys(properties).reduce((acc, key) => {
              if (key.includes('crm')) {
                acc[key] = properties[key]
              }
              return acc
            }, {}),
          }),
        ).pipe(
          switchMap((response) => {
            const stepUrl = getStepUrl(action.subProductName, 'contract')
            return from([
              OfferActions.contractDraftRequestSuccess(response.data.content),
              NavigationActions.onSetLastValidStep('contract', stepUrl),
              GeneralActions.onChangeRedirectUrl(stepUrl),
              GeneralActions.onChangeShowCallbackOnError(false),
            ])
          }),
          catchError((e) => {
            GuardXLogger.captureException(e, {
              extra: {
                action: 'CONTRACT_DRAFT_REQUEST',
                response: logDataSerializer(e.response),
                data: logDataSerializer(action.productDescriptorValues),
                calculatorName: action.calculatorName,
              },
            })
            return from([
              GeneralActions.onStopFetching(),
              ValidationActions.onChangeValidationSig(true),
              GeneralActions.onChangeShowCallbackOnError(true, e && e.response ? e.response : {}),
            ])
          }),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(OfferTypes.CONTRACT_DRAFT_ACCEPT_REQUEST),
      switchMap((action) => {
        const properties = state$.value.userData.getIn(['properties'], {})
        return from(
          SuriApi.acceptContractDraft(action.calculatorName, action.contractId, {
            ...action.data,
            // filter for properties
            properties: Object.keys(properties).reduce((acc, key) => {
              if (key.includes('crm')) {
                acc[key] = properties[key]
              }
              return acc
            }, {}),
          }),
        ).pipe(
          switchMap((response) => {
            const stepUrl = getStepUrl(action.subProductName, 'accept')
            return from([
              OfferActions.contractDraftAcceptRequestSuccess(response.data.content),
              NavigationActions.onSetLastValidStep('accept', stepUrl),
              NavigationActions.onSetLastValidStepExpire(
                moment(new Date())
                  .add(1, 'm')
                  .toDate(),
              ),
              GeneralActions.onChangeRedirectUrl(stepUrl),
              GeneralActions.onChangeShowCallbackOnError(false),
            ])
          }),
          catchError((e) => {
            GuardXLogger.captureException(e, {
              extra: {
                action: 'CONTRACT_DRAFT_ACCEPT_REQUEST',
                response: logDataSerializer(e.response),
                contractId: action.contractId,
                attachments: action.attachments,
                calculatorName: action.calculatorName,
              },
            })
            if (
              e &&
              (e.code === 'ECONNABORTED' ||
                (e.response && e.response.data && e.response.data.errorMessage === 'Contract is not in draft state.'))
            ) {
              const stepUrl = getStepUrl(action.subProductName, 'calculator')
              return from([
                GeneralActions.onStopFetching(),
                NavigationActions.onSetLastValidStep('calculator', stepUrl),
                GeneralActions.onChangeRedirectUrl(stepUrl, {
                  message: config.getConfig('isBackOffice') ? 'accept_contract_timeout_bo' : 'accept_contract_timeout',
                }),
              ])
            }
            return from([
              GeneralActions.onStopFetching(),
              GeneralActions.onChangeShowCallbackOnError(true, e && e.response ? e.response : {}),
            ])
          }),
        )
      }),
    ),
  (action$) =>
    action$.pipe(
      ofType(OfferTypes.CONTRACT_REQUEST),
      switchMap((action) =>
        from(SuriApi.getContract(action.contractId)).pipe(
          switchMap((response) => of(OfferActions.contractRequestSuccess(response.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.CALCULATOR_TEXT_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.calculatorStep4Content(action.calculatorName)).pipe(
          switchMap((response) =>
            of(
              ProductActions.calculatorTextContentRequestSuccess(
                response.data.data.textOrder ? response.data.data.textOrder : [],
                response.data.data.blocks ? response.data.data.blocks : {},
                response.data.data.alerts ? response.data.data.alerts : {},
              ),
            ),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(OfferTypes.PAYMENT_INFO_REQUEST),
      switchMap((action) =>
        from(SuriApi.getPaymentInfo(action.contractId)).pipe(
          switchMap((response) => of(OfferActions.paymentInfoRequestSuccess(response.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
]

export default CalculatorEpics
